/**
 * AxLabs Simulation Core - Numerical Integrators
 *
 * Deterministic numerical integration methods for solving ODEs.
 * Each integrator is suitable for different types of physical systems.
 */
import { add2, scale2 } from './vector.js';
// ============================================================================
// EULER INTEGRATION
// ============================================================================
/**
 * Semi-implicit Euler integrator (Symplectic Euler)
 *
 * Good for: Simple systems, educational purposes
 * Stability: First-order, can accumulate energy over time
 *
 * Updates velocity first, then position using the new velocity.
 * This is more stable than explicit Euler for oscillatory systems.
 */
export function eulerStep(position, velocity, acceleration, dt) {
    // Update velocity first (using old position's acceleration)
    const newVelocity = add2(velocity, scale2(acceleration, dt));
    // Update position using new velocity (semi-implicit)
    const newPosition = add2(position, scale2(newVelocity, dt));
    return { position: newPosition, velocity: newVelocity };
}
/**
 * Explicit Euler for generic state vectors
 */
export function eulerStepGeneric(state, derivative, dt) {
    const deriv = derivative(state);
    const newState = { ...state };
    for (const key of Object.keys(deriv)) {
        const dValue = deriv[key];
        if (typeof dValue === 'number' && typeof state[key] === 'number') {
            newState[key] =
                state[key] + dValue * dt;
        }
    }
    return newState;
}
// ============================================================================
// VERLET INTEGRATION
// ============================================================================
/**
 * Velocity Verlet integrator
 *
 * Good for: Oscillatory systems (pendulums, springs), molecular dynamics
 * Stability: Second-order, excellent energy conservation for conservative forces
 *
 * This is the recommended integrator for most physics simulations.
 */
export function verletStep(position, velocity, acceleration, getAcceleration, dt) {
    // Half-step velocity
    const halfVelocity = add2(velocity, scale2(acceleration, dt / 2));
    // Full-step position
    const newPosition = add2(position, scale2(halfVelocity, dt));
    // Calculate new acceleration at new position
    const newAcceleration = getAcceleration(newPosition, halfVelocity);
    // Complete velocity step
    const newVelocity = add2(halfVelocity, scale2(newAcceleration, dt / 2));
    return { position: newPosition, velocity: newVelocity };
}
/**
 * Störmer-Verlet (position-based) integrator
 *
 * Uses only positions (no explicit velocity storage).
 * Good for constraint-based systems.
 */
export function stormerVerletStep(currentPosition, previousPosition, acceleration, dt) {
    // x(t+dt) = 2*x(t) - x(t-dt) + a(t)*dt²
    return {
        x: 2 * currentPosition.x - previousPosition.x + acceleration.x * dt * dt,
        y: 2 * currentPosition.y - previousPosition.y + acceleration.y * dt * dt,
    };
}
// ============================================================================
// RUNGE-KUTTA 4 (RK4) INTEGRATION
// ============================================================================
/**
 * Fourth-order Runge-Kutta integrator
 *
 * Good for: Smooth ODEs, chemical kinetics, population dynamics
 * Stability: Fourth-order accuracy, excellent for non-oscillatory systems
 * Cost: 4 derivative evaluations per step
 *
 * This is the most accurate general-purpose integrator.
 */
export function rk4Step(state, derivative, t, dt, add, scale) {
    // k1 = f(t, y)
    const k1 = derivative(state, t);
    // k2 = f(t + dt/2, y + dt/2 * k1)
    const state2 = add(state, scale(k1, dt / 2));
    const k2 = derivative(state2, t + dt / 2);
    // k3 = f(t + dt/2, y + dt/2 * k2)
    const state3 = add(state, scale(k2, dt / 2));
    const k3 = derivative(state3, t + dt / 2);
    // k4 = f(t + dt, y + dt * k3)
    const state4 = add(state, scale(k3, dt));
    const k4 = derivative(state4, t + dt);
    // y(t+dt) = y(t) + dt/6 * (k1 + 2*k2 + 2*k3 + k4)
    const k2Scaled = scale(k2, 2);
    const k3Scaled = scale(k3, 2);
    const sum1 = add(k1, k2Scaled);
    const sum2 = add(sum1, k3Scaled);
    const sum3 = add(sum2, k4);
    return add(state, scale(sum3, dt / 6));
}
/**
 * RK4 for numeric record states (most common case)
 */
export function rk4StepNumeric(state, derivative, t, dt) {
    const addStates = (a, b) => {
        const result = { ...a };
        for (const key of Object.keys(b)) {
            if (typeof a[key] === 'number' && typeof b[key] === 'number') {
                result[key] =
                    a[key] + b[key];
            }
        }
        return result;
    };
    const scaleState = (s, k) => {
        const result = { ...s };
        for (const key of Object.keys(s)) {
            if (typeof s[key] === 'number') {
                result[key] = s[key] * k;
            }
        }
        return result;
    };
    return rk4Step(state, derivative, t, dt, addStates, scaleState);
}
// ============================================================================
// LEAPFROG INTEGRATION
// ============================================================================
/**
 * Leapfrog integrator
 *
 * Good for: Long-term simulations, N-body problems
 * Stability: Second-order, symplectic (preserves phase space volume)
 *
 * Velocity and position are evaluated at alternating half-steps.
 */
export function leapfrogStep(position, velocity, // velocity at t - dt/2
acceleration, dt) {
    // Kick: v(t+dt/2) = v(t-dt/2) + a(t)*dt
    const newVelocity = add2(velocity, scale2(acceleration, dt));
    // Drift: x(t+dt) = x(t) + v(t+dt/2)*dt
    const newPosition = add2(position, scale2(newVelocity, dt));
    return { position: newPosition, velocity: newVelocity };
}
// ============================================================================
// ADAPTIVE STEP SIZE
// ============================================================================
/**
 * Error estimate for adaptive step size control
 * Compares RK4 result with two half-steps
 */
export function adaptiveRK4Step(state, derivative, t, dt, tolerance) {
    // Full step
    const fullStep = rk4StepNumeric(state, derivative, t, dt);
    // Two half steps
    const halfStep1 = rk4StepNumeric(state, derivative, t, dt / 2);
    const halfStep2 = rk4StepNumeric(halfStep1, derivative, t + dt / 2, dt / 2);
    // Estimate error
    let maxError = 0;
    for (const key of Object.keys(fullStep)) {
        const err = Math.abs(fullStep[key] - halfStep2[key]);
        const scale = Math.abs(halfStep2[key]) + 1e-10;
        maxError = Math.max(maxError, err / scale);
    }
    // Adjust step size
    let newDt = dt;
    if (maxError > tolerance) {
        // Reduce step size
        newDt = dt * Math.max(0.5, 0.9 * Math.pow(tolerance / maxError, 0.2));
    }
    else if (maxError < tolerance * 0.1) {
        // Increase step size (but not too aggressively)
        newDt = dt * Math.min(2.0, 0.9 * Math.pow(tolerance / maxError, 0.2));
    }
    return { state: halfStep2, dt: newDt, error: maxError };
}
//# sourceMappingURL=integrators.js.map